"use client";

import { useMediaQuery } from "@/app/(site)/_hooks/useMediaQuery";
import { AboutPageQueryResult } from "@/sanity.types";
import { urlFor } from "@/sanity/lib/image";
import Image from "next/image";
import { motion, useScroll, useMotionValueEvent, useTransform } from "framer-motion";
import { useRef, useState } from "react";

type ValueItemProps = {
	value: NonNullable<AboutPageQueryResult>["companyValues"]["values"][number];
	index: number;
	onScrollProgress: (index: number, progress: number) => void;
	scrollProgressArray: number[];
	totalItems: number;
};

const ValueItem = ({ value, index, onScrollProgress, scrollProgressArray, totalItems }: ValueItemProps) => {
	const elementRef = useRef<HTMLDivElement>(null);

	const { scrollYProgress } = useScroll({
		target: elementRef,
		offset: ["start center", "end center"],
	});

	useMotionValueEvent(scrollYProgress, "change", (progress) => {
		onScrollProgress(index, progress);
	});

	const getAnimationValues = (progress: number) => {
		const nextProgress = index < totalItems - 1 ? scrollProgressArray[index + 1] || 0 : 0;
		const nextScaledProgress = Math.min(1, nextProgress * 2);
		const currentScaledProgress = Math.min(1, progress * 2);

		let effectiveProgress = currentScaledProgress;

		if (nextScaledProgress > 0) {
			effectiveProgress = Math.max(0, currentScaledProgress - nextScaledProgress);
		}

		const distanceFromVisible = 1 - effectiveProgress;
		const easedDistance = Math.pow(distanceFromVisible, 0.8);
		const scale = 0.85 + effectiveProgress * 0.15;
		const blur = easedDistance * 8;
		const opacity = 0.25 + effectiveProgress * 0.75;
		const translateY = easedDistance * 20;

		return { scale, blur, opacity, translateY };
	};

	const blur = useTransform(scrollYProgress, (progress: number) => `blur(${getAnimationValues(progress).blur}px)`);
	const opacity = useTransform(scrollYProgress, (progress: number) => getAnimationValues(progress).opacity);
	const transform = useTransform(scrollYProgress, (progress: number) => {
		const { translateY, scale } = getAnimationValues(progress);
		return `translate3d(0px, ${translateY}px, 0px) scale(${scale}, ${scale})`;
	});

	return (
		<motion.div ref={elementRef} className="col-span-full grid h-[32.5rem] grid-cols-12 items-center gap-x-5">
			<motion.div className="col-span-4 md:col-span-3 md:col-start-2" style={{ filter: blur, opacity, transform }}>
				<h2 className="text-h2 text-graphite-black translate-y-[6.25rem]">{value.headline}</h2>
			</motion.div>

			<motion.div className="col-span-4 col-start-9" style={{ filter: blur, opacity, transform }}>
				<p className="text-body-m text-graphite-black/70 translate-y-[6.25rem]">{value.text}</p>
			</motion.div>
		</motion.div>
	);
};

type OurValuesDesktopProps = {
	values: NonNullable<AboutPageQueryResult>["companyValues"]["values"];
};

export default function OurValuesDesktop({ values }: OurValuesDesktopProps) {
	const isDesktop = useMediaQuery("(min-width: 990px)");
	const containerRef = useRef<HTMLDivElement>(null);
	const mediaHolderRef = useRef<HTMLDivElement>(null);
	const [scrollProgress, setScrollProgress] = useState<number[]>(new Array(values.length).fill(0));

	const handleScrollProgress = (index: number, progress: number) => {
		setScrollProgress((prev) => {
			const newProgress = [...prev];
			newProgress[index] = progress;
			return newProgress;
		});
	};

	const getClipPath = (imageIndex: number) => {
		const currentProgress = scrollProgress[imageIndex] || 0;

		// Scale progress so that 0.5 becomes 1.0 (full reveal when text is centered)
		const scaledProgress = Math.min(1, currentProgress * 2);

		if (imageIndex === 0) {
			// First image: starts visible, gets clipped from bottom as we scroll to second
			const nextProgress = scrollProgress[1] || 0;
			const nextScaledProgress = Math.min(1, nextProgress * 2);

			if (nextScaledProgress > 0) {
				const clipPercentage = Math.min(100, nextScaledProgress * 100);
				return `inset(0 0 ${clipPercentage}% 0)`;
			}
			return "inset(0 0 0 0)";
		} else {
			// Subsequent images: reveal from top based on their scroll progress
			if (scaledProgress > 0) {
				const clipPercentage = Math.max(0, (1 - scaledProgress) * 100);
				return `inset(${clipPercentage}% 0 0 0)`;
			}
			return "inset(100% 0 0 0)";
		}
	};

	if (isDesktop) {
		return (
			<div
				ref={containerRef}
				className="grid grid-cols-12 items-center gap-x-5 [&>div:first-child]:row-start-1 [&>div:first-child]:-mt-[9.375rem]"
			>
				{values.map((value, index) => (
					<ValueItem
						key={value._key}
						value={value}
						index={index}
						onScrollProgress={handleScrollProgress}
						scrollProgressArray={scrollProgress}
						totalItems={values.length}
					/>
				))}

				<div className="pin-spacer sticky top-30 col-start-5 -col-end-5 row-span-full row-start-1 grid">
					<motion.div ref={mediaHolderRef} className="media-holder col-span-4 mt-[6.719rem] grid grid-cols-4 grid-rows-1">
						<div className="relative col-span-2 col-start-2 aspect-[256/320] overflow-hidden rounded-lg">
							{values.map((value, index) => (
								<motion.div
									key={value._key}
									className="absolute top-0 left-0 h-full w-full overflow-hidden rounded-lg"
									style={{ clipPath: getClipPath(index) }}
								>
									<div className="relative h-full w-full">
										<Image
											src={urlFor(value.image).url()}
											alt={value.headline}
											fill
											className="h-full w-full object-cover"
											sizes="33vw"
										/>
										<div className="bg-graphite-black/20 absolute inset-0" />
									</div>
								</motion.div>
							))}
						</div>
					</motion.div>
				</div>
			</div>
		);
	}

	return null;
}
